/**
 * AIghtBet Frontend — Runtime feature flags (PostHog)
 * ===================================================
 * Runtime, user-targeted feature flags evaluated through the PostHog
 * browser SDK. Source of truth for flags that change without a deploy.
 *
 * KAN-69 / KAN-127: We deliberately use PostHog's flag-eval API
 * (`posthog.isFeatureEnabled`, `posthog.onFeatureFlags`) here. This is
 * the chosen runtime-flag system going forward — we already pay for
 * PostHog for product analytics, the flag-eval API is part of the same
 * SDK we initialize in `src/posthog.ts`, and standing up a second
 * provider would be pure tax. Build-time flags (sport rollout, env-gated
 * features) live in `src/config/featureFlags.tsx` and are NOT migrated
 * here — they're compiled into the bundle and don't need runtime eval.
 *
 * KAN-128 coordination: PostHog init is gated on consent. Until consent
 * is granted, `posthog.__loaded` is false and every helper here
 * fails-closed to the safe default (false for kill-switches like
 * PROPS_ENABLED). Do NOT change this without re-reading KAN-128 — a
 * flag default-true would expose paused features pre-consent.
 */

import posthog from 'posthog-js';
import { useEffect, useState } from 'react';

/**
 * Known runtime flag keys. Adding a new flag? Add it here so the type
 * checker catches typos at every call site.
 */
export type RuntimeFlagKey =
  | 'PROPS_ENABLED' // KAN-69: gate the props UI + backend pipeline.
  | 'marketing_landing_v1' // KAN-3124: gate the new marketing landing surface (Phase 3).
  | 'onboarding_tour_v1' // KAN-3057: gate the first-run onboarding tour. Default OFF; cohort-flipped from PostHog.
  | 'new-all-sports-tab' // ADR-0005 Phase 1: KAN-26248 All Sports chip ships dark for retrofit; flip to 1% then ramp.
  | 'revenuecat-iap' // ADR-0005 Phase 1: future RevenueCat IAP integration kill-switch; default OFF until wired.
  | 'predict-game-v2' // ADR-0005 Phase 1: future Stage 2 rewrite; default OFF; per-sport ramp via PostHog conditions.
  | 'honest-failure-surfaces' // ADR-0005 Phase 1: feedback-no-fallback-no-mock pattern (predictions temporarily unavailable); default ON.
  | 'kill-switch-predictions'; // ADR-0005 Phase 1: emergency one-flip disable of predictions surfaces (KAN-26245 lesson). Default OFF.

/**
 * Synchronous flag check. Returns the current value of the flag from
 * the active PostHog client, or `defaultValue` if PostHog isn't loaded
 * yet (pre-consent per KAN-128, missing API key, init failure).
 *
 * For React render code prefer `useFeatureFlag` — it re-renders when
 * PostHog finishes loading or the flag changes. Use this synchronous
 * helper in non-render code (array filters, conditionals in handlers,
 * module-level guards).
 *
 * The default for kill-switch flags MUST be `false`. A flag that
 * returns `true` while PostHog is still booting would expose paused
 * features for the first few hundred ms of every page load.
 */
export function isFeatureEnabled(
  key: RuntimeFlagKey,
  defaultValue: boolean = false,
): boolean {
  try {
    // posthog-js exposes `__loaded` once `posthog.init` resolves. Before
    // that, `isFeatureEnabled` returns undefined and we want the safe
    // default — never an "assume true" optimistic answer.
    if (!posthog || !(posthog as { __loaded?: boolean }).__loaded) {
      return defaultValue;
    }
    const v = posthog.isFeatureEnabled(key);
    return typeof v === 'boolean' ? v : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * React hook — re-renders when PostHog finishes loading or the flag's
 * evaluated value changes. Subscribes via `posthog.onFeatureFlags`,
 * which fires on initial flag load and on every subsequent reload
 * (e.g. after `posthog.reloadFeatureFlags()`).
 */
export function useFeatureFlag(
  key: RuntimeFlagKey,
  defaultValue: boolean = false,
): boolean {
  const [enabled, setEnabled] = useState<boolean>(() =>
    isFeatureEnabled(key, defaultValue),
  );

  useEffect(() => {
    // Re-read on mount in case PostHog finished loading between
    // useState's lazy initializer and the effect.
    setEnabled(isFeatureEnabled(key, defaultValue));

    let unsubscribe: (() => void) | undefined;
    try {
      if (posthog && typeof posthog.onFeatureFlags === 'function') {
        unsubscribe = posthog.onFeatureFlags(() => {
          setEnabled(isFeatureEnabled(key, defaultValue));
        });
      }
    } catch {
      /* no-op */
    }

    return () => {
      try {
        unsubscribe?.();
      } catch {
        /* no-op */
      }
    };
  }, [key, defaultValue]);

  return enabled;
}

/**
 * Convenience wrappers for the only flag wired today. Add new wrappers
 * as flags land so call sites stay readable.
 */
export const isPropsEnabled = (): boolean => isFeatureEnabled('PROPS_ENABLED', false);
export const usePropsEnabled = (): boolean => useFeatureFlag('PROPS_ENABLED', false);

/**
 * KAN-3124 — Marketing landing v1. Default ON since KAN-25467 (2026-05-21):
 * the existing UltimateCleanHub-at-/ behavior gave anon visitors zero
 * orientation vs every competitor's real landing page. Flag-flip approach
 * preserves the rollback path (set false in PostHog if regression surfaces)
 * without requiring a code revert.
 *
 * Original Phase 3 (KAN-3125) gated the net-new marketing landing route on
 * this flag so Phase 2 could ship without changing live `/`. Phase 4
 * (KAN-25467) flips the default ON now that the page itself is verified
 * spec-compliant: 18+ disclaimer (not 21+), no player-props copy,
 * PREDICTION verb, Wordmark SVG, real verified-record numbers.
 */
export const isMarketingLandingV1Enabled = (): boolean =>
  isFeatureEnabled('marketing_landing_v1', true);
export const useMarketingLandingV1 = (): boolean =>
  useFeatureFlag('marketing_landing_v1', true);

/**
 * KAN-3057 — Onboarding tour v1. Default off; Phase 3 mounts
 * `OnboardingTourGate` globally but only fires for users in the flag's
 * enrolled cohort. Will flips this per-cohort from PostHog after Phase 4
 * UAT closes.
 */
export const isOnboardingTourV1Enabled = (): boolean =>
  isFeatureEnabled('onboarding_tour_v1', false);
export const useOnboardingTourV1 = (): boolean =>
  useFeatureFlag('onboarding_tour_v1', false);

/**
 * ADR-0005 Phase 1 — risky-change kill-switches and dark-ship gates.
 *
 * `new-all-sports-tab` (default OFF): wraps the KAN-26248 "All sports"
 * chip retrofit so the UI ships dark, gets flipped to 1% in PostHog,
 * monitored, then ramped. Toggling false reverts to the pre-26248
 * sport-only chip row without a deploy.
 *
 * `revenuecat-iap` (default OFF): will gate the future RevenueCat IAP
 * integration so backend webhooks + frontend purchase buttons can land
 * dark before flip-on.
 *
 * `predict-game-v2` (default OFF): will gate the future Stage 2
 * rewrites so they can roll per-sport (PostHog cohort conditions on a
 * `sport` user-property) without per-sport deploys.
 *
 * `honest-failure-surfaces` (default ON): toggles the
 * "predictions temporarily unavailable" pattern per
 * feedback-no-fallback-no-mock. Default ON so honest surfaces are the
 * standing behavior; can be flipped OFF for incident-mode soft hide.
 *
 * `kill-switch-predictions` (default OFF): emergency one-flip global
 * disable on the predictions surfaces. KAN-26245-class regressions can
 * be muted user-side from PostHog without waiting on a Stage 2 fix.
 */
export const isNewAllSportsTabEnabled = (): boolean =>
  isFeatureEnabled('new-all-sports-tab', false);
export const useNewAllSportsTab = (): boolean =>
  useFeatureFlag('new-all-sports-tab', false);

export const isRevenueCatIapEnabled = (): boolean =>
  isFeatureEnabled('revenuecat-iap', false);
export const useRevenueCatIap = (): boolean =>
  useFeatureFlag('revenuecat-iap', false);

export const isPredictGameV2Enabled = (): boolean =>
  isFeatureEnabled('predict-game-v2', false);
export const usePredictGameV2 = (): boolean =>
  useFeatureFlag('predict-game-v2', false);

export const isHonestFailureSurfacesEnabled = (): boolean =>
  isFeatureEnabled('honest-failure-surfaces', true);
export const useHonestFailureSurfaces = (): boolean =>
  useFeatureFlag('honest-failure-surfaces', true);

export const isKillSwitchPredictionsActive = (): boolean =>
  isFeatureEnabled('kill-switch-predictions', false);
export const useKillSwitchPredictions = (): boolean =>
  useFeatureFlag('kill-switch-predictions', false);
